import { useEffect, useMemo, useRef, useState } from "react";
import { get, useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { ReactComponent as ChevronDownIcon } from "assets/images/iconsV2/chevron-down.svg";
import { ReactComponent as ChevronUpIcon } from "assets/images/iconsV2/chevron-up.svg";
import { ReactComponent as SearchIcon } from "assets/images/icons/Search.svg";
import Button from "components/v2/Button/Button";
import Checkbox from "components/v2/Checkbox/Checkbox";
import testIds from "constants/testIds";
import Input from "components/v2/Input/Input";
import { classNames } from "helpers/commonHelpers";
import FormSectionLabel from "pages/shipper/RequestFormV2/components/FormSectionLabel/FormSectionLabel";
import FieldErrorBadge from "@RequestFormV2/components/Badges/FieldErrorBadge";
import { INVITED_PROVIDERS_ERROR_PATH } from "@RequestFormV2/validation/sections/validateProvider";
import { useReactiveReviewClear } from "@RequestFormV2/hooks/useReactiveReviewClear";

import { useProvider } from "@RequestFormV2/sections/Provider/context";
import { isProviderEntrySelected } from "@RequestFormV2/sections/Provider/utils/getProviderSelectionKey";
import { isAllProvidersSelected } from "@RequestFormV2/sections/Provider/utils/providerSelection";
import { trackQuoteProviderFiltered } from "@RequestFormV2/formEvents/requestFormAnalytics";
import ProviderCard from "@RequestFormV2/sections/Provider/components/ProviderSelectionSection/ProviderCard";

import "./ProviderSelectionSection.scss";

const INITIAL_DISPLAY_COUNT = 9;

export default function ProviderSelectionSection() {
  const intl = useIntl();
  const {
    formState: { errors },
  } = useFormContext();
  const {
    providerOptions,
    selectedProviders,
    selectedParcelAccounts,
    allProvidersRequired,
    isAllProvidersMandatory,
    handleToggleProvider,
    handleParcelAccountChange,
    handleSelectAll,
  } = useProvider();
  const [filterText, setFilterText] = useState("");
  const [showAll, setShowAll] = useState(false);
  const scheduleStops = useWatch({ name: "schedule.stops" }) ?? [];
  const showRateCardBadge = scheduleStops.length <= 2;
  const selectedProvidersRef = useRef(selectedProviders);
  const providerOptionsRef = useRef(providerOptions);

  selectedProvidersRef.current = selectedProviders;
  providerOptionsRef.current = providerOptions;

  const filteredProviders = useMemo(
    () => providerOptions.filter((p) => p.company_name.toLowerCase().includes(filterText.toLowerCase())),
    [providerOptions, filterText],
  );
  useEffect(() => {
    setShowAll(false);
  }, [filterText]);

  useEffect(() => {
    const trimmedFilter = filterText.trim();
    if (!trimmedFilter) return undefined;

    const timer = window.setTimeout(() => {
      const filtered = providerOptionsRef.current.filter((provider) =>
        provider.company_name.toLowerCase().includes(trimmedFilter.toLowerCase()),
      );
      const selectedFilteredCount = filtered.filter((provider) =>
        isProviderEntrySelected(provider, selectedProvidersRef.current),
      ).length;

      trackQuoteProviderFiltered({
        filterCount: 1,
        selectFilteredProviderCount: selectedFilteredCount,
      });
    }, 500);

    return () => window.clearTimeout(timer);
  }, [filterText]);

  const hiddenCount = filteredProviders.length - INITIAL_DISPLAY_COUNT;
  const allSelected = isAllProvidersSelected(filteredProviders, selectedProviders, selectedParcelAccounts);
  const providerSelectionError = get(errors, INVITED_PROVIDERS_ERROR_PATH);

  useReactiveReviewClear(
    INVITED_PROVIDERS_ERROR_PATH,
    () => selectedProviders.length > 0,
    [selectedProviders],
  );

  return (
    <div className="rfv2-form-section">
      <FormSectionLabel
        label={intl.formatMessage({
          id: "request-form.provider.providerSelection.label",
          defaultMessage: "Provider selection",
        })}
        required
      />

      <div className="rfv2-form-section__content">
        <div className="provider-selection__controls">
          <div className="provider-selection__select-all" data-testid={testIds.REQUEST_FORM_PROVIDER_SELECT_ALL_BUTTON}>
            <Checkbox
              onChange={() => handleSelectAll(filteredProviders)}
              checked={allSelected}
              disabled={allProvidersRequired && allSelected}
              label={intl.formatMessage({
                id: "request-form.provider.providerSelection.selectAll",
                defaultMessage: "Select all",
              })}
            />
            <span className="provider-selection__selected-count">
              {intl.formatMessage(
                {
                  id: "request-form.provider.providerSelection.selectedCount",
                  defaultMessage: "({count} selected)",
                },
                { count: selectedProviders.length },
              )}
            </span>
          </div>
          <div className="provider-selection__filter">
            <Input
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder={intl.formatMessage({
                id: "request-form.provider.providerSelection.filterPlaceholder",
                defaultMessage: "Filter provider",
              })}
              icon={<SearchIcon />}
              testId={testIds.REQUEST_FORM_PROVIDER_SEARCH}
            />
          </div>
        </div>

        <div
          className={classNames("provider-selection__grid-wrapper", {
            "provider-selection__grid-wrapper--collapsed": !showAll && hiddenCount > 0,
          })}
        >
          <div className="provider-selection__grid">
            {filteredProviders.map((provider) => (
              <ProviderCard
                key={provider.id}
                provider={provider}
                selected={isProviderEntrySelected(provider, selectedProviders)}
                selectedParcelAccounts={selectedParcelAccounts}
                allProvidersRequired={allProvidersRequired}
                isAllProvidersMandatory={isAllProvidersMandatory}
                onToggle={handleToggleProvider}
                onParcelAccountChange={handleParcelAccountChange}
                showRateCardBadge={showRateCardBadge}
              />
            ))}
          </div>
        </div>

        {hiddenCount > 0 && (
          <div className="provider-selection__show-all">
            {showAll ? (
              <Button
                variant="tertiary-gray"
                size="sm"
                iconLeft={<ChevronUpIcon />}
                label={intl.formatMessage({
                  id: "request-form.provider.providerSelection.collapseAll",
                  defaultMessage: "Collapse all",
                })}
                onClick={() => setShowAll(false)}
              />
            ) : (
              <Button
                variant="tertiary-gray"
                size="sm"
                iconLeft={<ChevronDownIcon />}
                label={intl.formatMessage(
                  {
                    id: "request-form.provider.providerSelection.showAll",
                    defaultMessage: "Show all {count}",
                  },
                  { count: filteredProviders.length },
                )}
                onClick={() => setShowAll(true)}
              />
            )}
          </div>
        )}

        {providerSelectionError?.message && <FieldErrorBadge message={String(providerSelectionError.message)} />}
      </div>
    </div>
  );
}
